import { Divide, Equal, PieChart } from "lucide-react";

const EXAMPLE = { balance: 2500000, supply: 250000000, batch: 5000 };

export default function AllocationMath() {
  const share = (EXAMPLE.balance / EXAMPLE.supply) * 100;
  const payout = (EXAMPLE.batch * EXAMPLE.balance) / EXAMPLE.supply;

  return (
    <section className="py-24 px-6 bg-[#090612] border-b border-white/5" data-testid="allocation-math">
      <div className="max-w-4xl mx-auto">
        <div className="text-center max-w-2xl mx-auto mb-14">
          <span className="mono-font text-xs uppercase tracking-widest text-[#9945FF] font-semibold">The Math, Simplified</span>
          <h2 className="display-font uppercase text-3xl md:text-5xl text-white mt-2">How Allocations Are Calculated</h2>
          <p className="text-sm text-[#a8a1c2] mt-3">
            Eligible holders receive proportional allocations based on the published eligibility and snapshot rules. One formula, no discretion.
          </p>
        </div>

        <div className="glass-panel rounded-3xl p-8 md:p-10">
          {/* Formula */}
          <div className="flex flex-col md:flex-row items-center justify-center gap-4 md:gap-6 mb-10 text-center">
            <div className="p-4 rounded-2xl bg-black/40 border border-[#9945FF]/30 min-w-[170px]" data-testid="math-step-balance">
              <div className="text-[10px] mono-font uppercase text-[#9b93b8] mb-1">Your snapshot balance</div>
              <div className="mono-font text-lg font-bold text-white">2,500,000 <span className="text-[#9945FF]">$BASK</span></div>
            </div>
            <Divide size={18} className="text-[#14F195] rotate-90 md:rotate-0" />
            <div className="p-4 rounded-2xl bg-black/40 border border-[#00F0FF]/30 min-w-[170px]" data-testid="math-step-supply">
              <div className="text-[10px] mono-font uppercase text-[#9b93b8] mb-1">Eligible supply</div>
              <div className="mono-font text-lg font-bold text-white">250,000,000 <span className="text-[#00F0FF]">$BASK</span></div>
            </div>
            <Equal size={18} className="text-[#14F195] rotate-90 md:rotate-0" />
            <div className="p-4 rounded-2xl bg-[#14F195]/10 border border-[#14F195]/40 min-w-[170px]" data-testid="math-step-share">
              <div className="text-[10px] mono-font uppercase text-[#9b93b8] mb-1">Your share</div>
              <div className="mono-font text-lg font-bold text-[#14F195]">{share}%</div>
            </div>
          </div>

          {/* Visual bar */}
          <div className="mb-8">
            <div className="flex justify-between text-[10px] mono-font text-[#9b93b8] uppercase mb-2">
              <span>Example batch: ${EXAMPLE.batch.toLocaleString()} of basket assets</span>
              <span className="text-[#14F195]">Your allocation: ${payout.toLocaleString()} (Demo)</span>
            </div>
            <div className="h-4 rounded-full bg-white/5 overflow-hidden relative">
              <div className="absolute inset-y-0 left-0 rounded-full alloc-bar"
                style={{ width: `${share}%`, minWidth: "8px", background: "linear-gradient(90deg,#9945FF,#00F0FF,#14F195)", boxShadow: "0 0 12px rgba(20,241,149,0.4)" }} />
            </div>
            <div className="flex justify-between text-[10px] mono-font text-[#7a7493] mt-2">
              <span>Other eligible holders: {(100 - share).toFixed(1)}%</span>
              <span className="text-[#14F195]">You: {share}%</span>
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 text-center">
            {[
              { icon: PieChart, title: "Proportional", text: "Your share of each batch equals your share of eligible $BASK at snapshot time." },
              { icon: Divide, title: "Every Asset", text: "The same percentage applies to every asset in the batch — SOL, JUP, BONK, WIF and RAY alike." },
              { icon: Equal, title: "Published Rules", text: "Thresholds and snapshot cadence are fixed in advance so anyone can verify the math." },
            ].map((c) => (
              <div key={c.title} className="p-4 rounded-2xl bg-black/30 border border-white/10">
                <c.icon size={18} className="text-[#14F195] mx-auto mb-2" />
                <div className="text-sm font-bold text-white mb-1">{c.title}</div>
                <p className="text-[11px] text-[#b5aed0] leading-relaxed">{c.text}</p>
              </div>
            ))}
          </div>

          <p className="text-[11px] text-[#ffaa00] mono-font mt-6 text-center">
            * Worked example with demo figures. Not a promise of returns — asset values fluctuate and nothing is guaranteed.
          </p>
        </div>
      </div>
    </section>
  );
}
